import {
  ProductDetailsModal,
  ProductDetailsMap,
  AddInfo,
  ProductCardModal,
  NewProductModal,
  CreateProductModalState,
  ProductVariant,
  VariantDraft,
  InfoTemplateId,
  CategoryAttributeConfig,
  KeyAttributes,
} from "../../../interfaces/ProductObject";
import { convertRawMediaList } from "./mediaConvertor";
import { isInfoListingType } from "../../helpers/InfoListingHelper";

/**
 * Media list off ANY product shape — converted (mediaResources) or raw payload
 * (productMediaResources / snake_case variants; search endpoints store response
 * JSON directly). Entries may be raw ({ mediaType, objectPath, ... }) rather
 * than full MediaRecords — consumers must normalize via convertRawMediaList
 * (ProductImageHelper does). Typed as MediaRecord[] for carrier-field
 * compatibility.
 */
export function rawMediaResourcesOf(
  product: unknown,
): ProductDetailsModal["mediaResources"] {
  const p = product as Record<string, any> | null | undefined;
  const source =
    p?.mediaResources ??
    p?.productMediaResources ??
    p?.product_media_resources ??
    p?.media_resources;
  return Array.isArray(source) ? source : undefined;
}

/**
 * Normalizes a product's raw `variants` array to ProductVariant[], tolerant
 * of snake_case field names the way the rest of this file is (some payloads
 * this codebase already receives mix cases across endpoints). Returns
 * undefined for a non-variant product so `hasVariants`/`variants` stay in
 * sync rather than one being true and the other an empty array.
 */
export function normalizeRawVariants(
  raw: unknown,
): ProductVariant[] | undefined {
  const list = (raw as Record<string, any> | null | undefined)?.variants;
  if (!Array.isArray(list) || list.length === 0) return undefined;
  return list.map((v: any) => ({
    variantId: v.variant_id ?? v.variantId,
    label: v.label ?? "",
    price: Number(v.price) || 0,
    // Checked for null/undefined, not truthiness -- a `?? x ? ... : ...`
    // chain here would drop a legitimate originalPrice of 0.
    originalPrice:
      v.original_price != null || v.originalPrice != null
        ? Number(v.original_price ?? v.originalPrice)
        : undefined,
    sortOrder: Number(v.sort_order ?? v.sortOrder ?? 0),
    isDefault: (v.is_default ?? v.isDefault) === true,
    isAvailable: (v.is_available ?? v.isAvailable) !== false,
    sku: v.sku ?? undefined,
    details: v.details ?? undefined,
    mediaPaths: v.media_paths ?? v.mediaPaths ?? undefined,
  }));
}

export function convertRawProduct(
  raw: Record<string, unknown>,
): ProductDetailsModal {
  const media = raw.media as Record<string, unknown> | undefined;
  // Authoritative media list — backend field is `productMediaResources`
  // (nullable): [{ mediaType, objectPath, section, thumbNail }].
  // convertRawMediaList derives each item's publicUrl from its objectPath.
  const mediaResources = convertRawMediaList(
    raw.productMediaResources ??
      raw.product_media_resources ??
      raw.mediaResources ??
      raw.media_resources,
  );
  return {
    productId: (raw.product_id ?? raw.productId) as string,
    title: (raw.title ?? raw.name) as string | undefined,
    description: raw.description as string | undefined,
    category: raw.category as string | undefined,
    condition: raw.condition as string | undefined,
    sellingPrice: (raw.selling_price ?? raw.sellingPrice) as number | undefined,
    sellerId: (raw.seller_id ?? raw.sellerId) as string | undefined,
    sellerName: (raw.seller_name ?? raw.sellerName) as string | undefined,
    sellerVerified: (raw.seller_verified ?? raw.sellerVerified) as
      | boolean
      | undefined,
    status: raw.status as string | undefined,
    // Kept so a SOLD listing still reads as sold (greyed + badge) on every
    // surface, including visitors' and signed-out views.
    listingStatus: (raw.listing_status ?? raw.listingStatus) as
      | string
      | undefined,
    listingType: (raw.listing_type ?? raw.listingType) as string | undefined,
    businessId: (raw.business_id ?? raw.businessId) as string | undefined,
    businessName: (raw.business_name ?? raw.businessName) as string | undefined,
    businessLogoUrl: (raw.business_logo_url ?? raw.businessLogoUrl) as
      | string
      | undefined,
    createdTime: (raw.created_time ?? raw.createdTime) as string | undefined,
    updatedAt: (raw.updated_at ?? raw.updatedAt) as string | undefined,
    // `details` is an OPEN map (section, brand, per-category attributes), so it
    // passes through whole rather than being picked apart. Omitting it here is
    // what silently gutted listing-collection clones: every consumer that reads
    // `product.details?.x` got undefined and fell through to a default.
    details: (raw.details ?? raw.product_details ?? raw.productDetails) as any,
    age: (raw.age as number | undefined) ?? undefined,
    location: raw.location as string | undefined,
    phone: raw.phone as string | undefined,
    contactMode: (raw.contact_mode ?? raw.contactMode) as
      | "AUTO"
      | "MANUAL"
      | undefined,
    displayPrice: (raw.display_price ?? raw.displayPrice) as
      | boolean
      | undefined,
    displayable: raw.displayable as boolean | undefined,
    showHide: (raw.show_hide ?? raw.showHide) as boolean | undefined,
    isNegotiable: (raw.is_negotiable ?? raw.isNegotiable) as
      | boolean
      | undefined,
    latitude: (raw.latitude as number | undefined) ?? undefined,
    longitude: (raw.longitude as number | undefined) ?? undefined,
    sellingPoints: (raw.selling_points ??
      raw.sellingPoints) as ProductDetailsModal["sellingPoints"],
    media: media
      ? {
          imageUrlList: (media.image_url_list ??
            media.imageUrlList ??
            []) as string[],
        }
      : undefined,
    ...(mediaResources.length > 0 ? { mediaResources } : {}),
    hasVariants: (raw.has_variants ?? raw.hasVariants) as boolean | undefined,
    variantAxisLabel: (raw.variant_axis_label ?? raw.variantAxisLabel) as
      | string
      | undefined,
    variants: normalizeRawVariants(raw),
    // Info listings only (null/absent elsewhere) -- passed through as-is.
    infoContent: (raw.info_content ??
      raw.infoContent) as ProductDetailsModal["infoContent"],
    sponsors: raw.sponsors as ProductDetailsModal["sponsors"],
  };
}

/** details.INFO_TEMPLATE (the backend upper-cases attribute keys on save). */
export function infoTemplateOf(
  product: { details?: Record<string, any> | null } | null | undefined,
): string | undefined {
  const details = product?.details;
  const value = details?.INFO_TEMPLATE ?? details?.info_template;
  return typeof value === "string" && value.trim()
    ? value.trim().toLowerCase()
    : undefined;
}

export const convertProductDetailsToAddInfo = (
  productDetails: ProductDetailsModal,
): any => {
  // Helper function to format age
  const formatAge = (age: number | undefined): string | undefined => {
    if (age === undefined || age === null) return undefined;
    if (age === 0) return "New"; // Show "New" for age 0 instead of hiding it
    return age.toString();
  };

  return {
    id: productDetails.productId,
    name: productDetails.title ?? "Unknown",
    price: productDetails.sellingPrice ?? -1,
    age: formatAge(productDetails.age) ?? "Not specified",
    description: productDetails.description ?? "No description available",
    sellerName: productDetails.sellerName ?? "Anonymous",
    sellerId: productDetails.sellerId,
    category: productDetails.category,
    condition: productDetails.condition,
    location:
      productDetails.location ?? (productDetails as any)?.details?.location,
    postedDate:
      productDetails.createdTime ??
      (productDetails as any)?.details?.CREATED_AT,
    sellerVerified:
      productDetails.sellerVerified ?? productDetails.verifiedUser,
    displayPrice: (productDetails as any).displayPrice ?? true,
  };
};

export const convertProductDetailsToProductCard = (
  productDetails: ProductDetailsModal,
): ProductCardModal => {
  return {
    productId: productDetails.productId ?? "unknown",
    title: productDetails.title ?? "Unnamed Product",
    price: productDetails.sellingPrice ?? 0,
    description: productDetails.description ?? "No description available",
    condition:
      productDetails.condition ??
      (productDetails as any)?.details?.["Condition"] ??
      (productDetails as any)?.details?.["condition"] ??
      (productDetails as any)?.keyAttributes?.mandatoryAttributes?.[
        "Condition"
      ],
    listingType: productDetails.listingType,
    offerCount:
      (productDetails as any)?.offerCounts ??
      productDetails.offers?.outStandingOffersCount ??
      0,
    sellerName: productDetails.sellerName ?? "Unknown Seller",
    sellerId: productDetails.sellerId,
    sellerVerified: productDetails.verifiedUser,
    businessId: productDetails.businessId,
    businessName: productDetails.businessName,
    businessLogoUrl: productDetails.businessLogoUrl,
    searchDistance: productDetails.searchDistance,
    imageUrl: getProductImages(productDetails),
    // Raw payloads may carry productMediaResources without having gone through
    // convertRawProduct (search endpoints store response JSON directly), so
    // tolerate both converted MediaRecords and raw {mediaType, objectPath}.
    mediaResources: rawMediaResourcesOf(productDetails),
    createdTime: productDetails.createdTime,
    displayPrice: (productDetails as any).displayPrice ?? true,
    showHide: productDetails.showHide,
    // Raw list payloads (MyListings/Business) are stored without convertRawProduct,
    // so tolerate the snake_case variant alongside the camelCase field.
    listingStatus:
      productDetails.listingStatus ?? (productDetails as any).listing_status,
    hasVariants:
      productDetails.hasVariants ?? (productDetails as any).has_variants,
    variantAxisLabel:
      productDetails.variantAxisLabel ??
      (productDetails as any).variant_axis_label,
    variants: productDetails.variants ?? normalizeRawVariants(productDetails),
    infoTemplate: infoTemplateOf(productDetails),
  };
};

/**
 * `details` keys the BACKEND owns — row identity, timestamps, lifecycle state
 * and engagement counters that happen to be stored inside the attribute map.
 *
 * Compared case-folded (all entries lowercase), because the map is free-form
 * and the same key arrives as `pid`, `PID` or `Pid` depending on who wrote it.
 *
 * Exported because two callers need the same answer and must not drift: the
 * edit modal hides these from the optional-attribute editor, and the listing-
 * collection clone strips them so a new product doesn't inherit the source
 * row's identity (see uiToBackend/ProductConvertor.buildBulkPayloadFromExistingProducts).
 */
export const SERVER_OWNED_DETAIL_KEYS = new Set([
  "created_at",
  "updated_at",
  "createdtime",
  "pid",
  "status",
  "listingstatus",
  "sellerid",
  "sellername",
  "sellerverified",
  "verifieduser",
  "searchdistance",
  "offercounts",
  "productqueries",
]);

/**
 * `details` keys that duplicate a first-class product field. The top-level
 * field is authoritative; these are hidden from the attribute editor so the
 * same value can't be edited in two places.
 */
export const FIRST_CLASS_DETAIL_KEYS = new Set([
  "images",
  "age",
  "condition",
  "location",
  "price",
  "title",
  "description",
  "category",
  "contactmode",
  "latitude",
  "longitude",
  "phone",
  // Has its own dedicated field (essentials.sectionId, read explicitly
  // below) rather than flowing through the free-form optional-attribute
  // editor — same reason as every other key in this set.
  "sectionid",
  // Info listings keep their template here; it lives in infoDraft.template, not the attribute editor.
  "info_template",
]);

export const convertProductDetailsToNewProduct = (
  productDetails: ProductDetailsModal,
): CreateProductModalState => {
  // Explicit annotation: without it, `productDetails.details || {}` infers as
  // `ProductDetailsMap | {}`, and the empty-object branch has no index
  // signature — every `details.someKey` read below would then fail to
  // compile. Annotating pins the whole expression to ProductDetailsMap,
  // which `{}` is trivially assignable to (every field is optional).
  const details: ProductDetailsMap = productDetails.details || {};
  const mandatoryAttributes: Record<string, string> = {};
  const optionalAttributes: Record<string, { key: string; value: string }> = {};

  // Internal keys to ignore (technical metadata only)
  const ignoredKeys = new Set([
    ...SERVER_OWNED_DETAIL_KEYS,
    ...FIRST_CLASS_DETAIL_KEYS,
  ]);

  if (details) {
    Object.entries(details).forEach(([key, value]) => {
      const lowerKey = key.toLowerCase();
      if (ignoredKeys.has(lowerKey)) {
        return;
      }

      // Map everything unknown to optionalAttributes initially
      const id = crypto.randomUUID();
      optionalAttributes[id] = { key, value: String(value) };
    });
  }

  // Handle specific fields explicitly with fallbacks from details
  if (
    productDetails.condition ||
    details.Condition ||
    details.condition ||
    (details as any).CONDITION
  ) {
    mandatoryAttributes["Condition"] =
      productDetails.condition ||
      details.Condition ||
      details.condition ||
      (details as any).CONDITION;
  }

  const category =
    productDetails.category ||
    details.category ||
    details.CATEGORY ||
    (details as any).Category ||
    "";

  const isInfo = isInfoListingType(productDetails.listingType);
  const infoTemplate = (infoTemplateOf(productDetails) ??
    "blank") as InfoTemplateId;
  const activeSponsor =
    (productDetails.sponsors ?? []).find(
      (s) => !!s && (s.slot ?? 1) === 1 && (s.status ?? "ACTIVE") === "ACTIVE",
    ) ?? null;

  return {
    productId: productDetails.productId,
    infoDraft: isInfo
      ? {
          template: ["article", "timetable", "blank"].includes(infoTemplate)
            ? infoTemplate
            : "blank",
          // Deep copy: the draft is edited in place and must not touch the details store.
          blocks: JSON.parse(
            JSON.stringify(productDetails.infoContent?.blocks ?? []),
          ),
          sponsor: activeSponsor ? { ...activeSponsor } : null,
          // The body only rides along when the fetch opted in (details page);
          // list payloads omit it. The editor refetches when this is false so a
          // save can never overwrite a page it never loaded.
          loaded: Array.isArray(productDetails.infoContent?.blocks),
        }
      : { template: "blank", blocks: [], sponsor: null, loaded: true },
    essentials: {
      // Existing media prefill as references — bytes are NEVER re-downloaded
      // or re-uploaded on edit. productMediaResources is the only source.
      media: convertRawMediaList(rawMediaResourcesOf(productDetails)).map(
        (record) => ({ status: "existing" as const, record }),
      ),
      listingType:
        (productDetails.listingType as any) || details.listingType || "",
      age: productDetails.age ?? details.AGE ?? details.age ?? "",
      sharedAttributes: {},
      businessId:
        productDetails.businessId ||
        details.business_id ||
        details.businessId ||
        undefined,
      // Preserve exactly what's on the wire: a real id or an explicit `null`
      // (both `!== undefined`) round-trip as-is; a key that's fully ABSENT
      // stays `undefined` here rather than being coerced to `null`, so
      // buildCreateProductPayload can tell "never had a section" (omit the
      // key on save) apart from "explicitly cleared" (send `null` on save) —
      // see that function's own comment for why the distinction matters.
      // Fixed wire casing (`sectionId`, always this backend, never a
      // free-text screen), so no case-folding needed here unlike
      // Condition/CATEGORY above.
      sectionId:
        details.sectionId !== undefined
          ? (details.sectionId ?? null)
          : undefined,
    },
    description: {
      title: productDetails.title ?? details.title ?? "",
      category: category,
      description: productDetails.description ?? details.description ?? "",
    },
    keyAttributes: {
      mandatoryAttributes,
      optionalAttributes,
    },
    strategy: {
      askPrice: productDetails.sellingPrice ?? details.sellingPrice ?? 0,
      originalPrice:
        (productDetails as any).originalPrice ?? details.originalPrice ?? "",
      suggestion: {} as any,
      contactMode:
        productDetails.contactMode ?? details.contactMode ?? "MANUAL",
      isNegotiable:
        (productDetails as any).isNegotiable ?? details.isNegotiable ?? false,
      rentalPricing:
        (productDetails as any).rentalPricing ?? details.rentalPricing ?? {},
      depositAmount: details.deposit_amount ?? "",
      displayPrice: (productDetails as any).displayPrice ?? true,
      // Variants: draft rows are seeded straight from the persisted variants
      // (price kept numeric — inputs coerce to string on their own) so a
      // save with no variant edits round-trips the same ids/values via
      // mergeVariants server-side instead of silently clearing them.
      variantsEnabled: !!productDetails.hasVariants,
      variantAxisLabel: productDetails.variantAxisLabel ?? "",
      variantDrafts: (productDetails.variants ?? []).map(
        (v): VariantDraft => ({
          variantId: v.variantId,
          label: v.label,
          price: v.price,
          originalPrice: v.originalPrice,
          isDefault: v.isDefault,
          isAvailable: v.isAvailable,
          sku: v.sku,
          mediaPaths: v.mediaPaths,
        }),
      ),
      variantsWereEnabledOnLoad: !!productDetails.hasVariants,
    },
    meta: {
      currentStep: 1,
      viewMode: "editing",
    },
  };
};

// View model and helpers for Product Details page
export const getProductImages = (product: ProductDetailsModal): string[] => {
  // Support multiple possible shapes: media.imageUrlList (array/string/object) or details.images fallback
  const candidate: any =
    (product as any)?.media?.imageUrlList ?? (product as any)?.details?.images;
  if (!candidate) return [];
  if (Array.isArray(candidate))
    return (candidate as any[]).filter(Boolean) as string[];
  if (typeof candidate === "string")
    return candidate
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  if (typeof candidate === "object")
    return (Object.values(candidate) as any[]).filter(Boolean) as string[];
  return [];
};

export const getLikesAndTrust = (
  product: ProductDetailsModal,
): { likesCount: number; isTrustedSeller: boolean } => {
  const likesCount =
    (product as any)?.offerCounts ??
    product.offers?.outStandingOffersCount ??
    0;
  // const isTrustedSeller = (product as any)?.details?.TRUSTED ?? true;
  const isTrustedSeller = false;
  return { likesCount, isTrustedSeller };
};

export const convertConfigAttributesToKeyAttributes = (
  configAttributes: CategoryAttributeConfig[] | undefined,
): KeyAttributes => {
  const result: KeyAttributes = {
    mandatoryAttributes: {},
    optionalAttributes: {},
  };
  for (const attr of configAttributes ?? []) {
    if (!attr.name) continue;
    if (attr.required) result.mandatoryAttributes[attr.name] = "";
    else
      result.optionalAttributes[`config:${attr.name}`] = {
        key: attr.name,
        value: "",
      };
  }
  return result;
};
